import { StatCard } from "@/components/ui/StatCard";
import { LoadingState, ErrorState, DemoDataNotice } from "@/components/ui/AsyncState";
import { useApi } from "@/lib/use-api";
import { formatCurrencyFromCents, formatPercent } from "@/lib/format";
import type { PlatformOverview } from "@platform/shared";

export function AdminHome() {
  const { data, isLoading, error, refetch } = useApi<{ overview: PlatformOverview }>("/api/admin/overview");

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl text-ink">Platform overview</h1>
        <p className="mt-1 text-sm text-ink-soft">Metrics across every customer organization.</p>
      </div>

      {isLoading && <LoadingState label="Loading platform metrics…" />}
      {error && <ErrorState message={error} onRetry={refetch} />}

      {data && (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <StatCard label="Total customers" value={String(data.overview.totalCustomers)} />
            <StatCard label="Active customers" value={String(data.overview.activeCustomers)} />
            <StatCard
              label="New customers"
              value={String(data.overview.newCustomers)}
              hint={`Trailing ${data.overview.rangeDays} days`}
            />
            <StatCard
              label="MRR"
              value={formatCurrencyFromCents(data.overview.mrrCents)}
              hint="Estimated from active plan list prices"
            />
            <StatCard
              label="Revenue"
              value={formatCurrencyFromCents(data.overview.revenueCents)}
              hint={`Trailing ${data.overview.rangeDays} days`}
            />
            <StatCard
              label="Expenses"
              value={formatCurrencyFromCents(data.overview.expensesCents)}
              hint={`Trailing ${data.overview.rangeDays} days`}
            />
            <StatCard
              label="Profit"
              value={formatCurrencyFromCents(data.overview.profitCents)}
              hint={`Trailing ${data.overview.rangeDays} days`}
            />
            <StatCard
              label="Churn"
              value={formatPercent(data.overview.churnRatePercent / 100)}
              hint="All-time, canceled ÷ total subscriptions"
            />
            <StatCard label="Total leads" value={String(data.overview.totalLeads)} />
            <StatCard label="AI conversations" value={String(data.overview.aiConversations)} />
            <StatCard label="Calls processed" value={String(data.overview.callsProcessed)} />
          </div>

          <DemoDataNotice>
            These are real counts and sums from the platform database. MRR is an estimate from active
            subscriptions' plan list prices — Stripe billing isn't connected yet, so no row anywhere stores an
            actual billed amount. Revenue and expenses come from the <code>platform_revenue</code> and{" "}
            <code>expenses</code> tables, which are only populated by seed data or manual entry until Stripe
            billing and telephony/AI usage tracking are built in later steps.
          </DemoDataNotice>
        </>
      )}
    </div>
  );
}
